<template>
  <v-card flat class="common-board-header-card" style="width: 100%; flex-shrink: 0;">
    <!-- Main Top Bar -->
    <div class="header-main-bar d-flex align-center justify-space-between px-4 py-2">
      <!-- Left: Title & Search -->
      <div class="d-flex align-center left-header-section">
        <span class="board-header-title">{{ boardName }}</span>

        <!-- Search Component -->
        <div class="search-wrapper d-flex align-center">
          <div v-if="searchClicked" ref="searchContainer" class="search-input-box">
            <v-text-field
              v-model="search"
              density="compact"
              variant="outlined"
              hide-details
              placeholder="Search by created date..."
              prepend-inner-icon="mdi-magnify"
              clearable
              autofocus
              class="header-search-field"
              @update:model-value="getSearch"
              @click:clear="clearSearch"
            ></v-text-field>
          </div>
          <v-btn
            v-else
            icon
            size="small"
            variant="text"
            color="grey-darken-2"
            class="search-toggle-btn"
            title="Search"
            @click.stop="toggleSearch"
          >
            <v-icon size="20">mdi-magnify</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Right: Clear Filter, Filter Menu, Past Due Switch, Export -->
      <div class="d-flex align-center right-header-section">
        <!-- Clear Filter Button (ONLY shown when filter is added) -->
        <v-btn
          v-if="hasActiveFilter"
          size="small"
          variant="tonal"
          color="error"
          class="text-none clear-filter-btn px-2"
          prepend-icon="mdi-filter-off"
          title="Clear all applied filters"
          @click="clearAllFilters"
        >
          Clear Filter
        </v-btn>

        <!-- Filter Menu Icon Button with active badge -->
        <v-menu
          v-model="menu"
          transition="scale-transition"
          location="bottom end"
          :close-on-content-click="false"
        >
          <template v-slot:activator="{ props }">
            <v-btn
              icon
              size="small"
              variant="text"
              v-bind="props"
              title="Filter options"
              class="filter-icon-btn"
            >
              <v-badge
                v-if="hasActiveFilter"
                dot
                color="primary"
                offset-x="2"
                offset-y="2"
              >
                <v-icon size="20" color="primary">mdi-filter</v-icon>
              </v-badge>
              <v-icon v-else size="20" color="grey-darken-2">mdi-filter-outline</v-icon>
            </v-btn>
          </template>
          <v-list width="360px" class="pa-0" rounded="lg" elevation="4">
            <DashBoardFilter
              :existFilterData="existFilterData || savedFilterData"
              :type="'openIssue'"
              :type2="'live'"
              @applyChange="applyChange"
              @clearFilter="clearAllFilters"
            />
          </v-list>
        </v-menu>

        <!-- Past Due Task Switch (Clean inline layout, no gap) -->
        <div class="d-flex align-center switch-box">
          <v-switch
            v-model="switchBtn"
            false-value="OFF"
            true-value="ON"
            color="primary"
            density="compact"
            hide-details
            class="header-switch"
            @update:model-value="getSwitchData"
          ></v-switch>
          <span class="switch-label" @click="togglePastDue">
            Past Due Task
          </span>
        </div>

        <!-- Export Dropdown Button -->
        <v-menu location="bottom end">
          <template v-slot:activator="{ props }">
            <v-btn
              icon
              size="small"
              variant="text"
              v-bind="props"
              title="Export"
              class="export-icon-btn"
            >
              <i class="pi pi-file-export" style="font-size: 1.15rem; color: #475569;"></i>
            </v-btn>
          </template>
          <v-list density="compact" elevation="3" rounded="lg">
            <v-list-item
              v-for="(item, index) in downloadMenu"
              :key="index"
              :value="index"
              @click.stop="exportFile(item)"
            >
              <template v-slot:prepend>
                <v-icon
                  size="small"
                  :color="item === 'Excel' ? 'success' : 'error'"
                  :icon="item === 'Excel' ? 'mdi-file-excel-outline' : 'mdi-file-pdf-box'"
                ></v-icon>
              </template>
              <v-list-item-title class="font-weight-medium text-body-2">{{ item }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <!-- Workspace Chips Bar with Horizontal Mouse Scrolling & Dragging -->
    <div
      v-if="chipName && chipName.length > 0"
      ref="chipsContainer"
      class="workspace-chips-bar px-4 pb-2 pt-1"
      @mousedown="startDrag"
    >
      <span
        v-for="(workspace, workspaceIndex) in chipName"
        :key="workspaceIndex"
        class="workspace show-cursor"
        :class="{ 'workspace-selected': selectedChipIndex === workspaceIndex }"
        @click.stop="handleChipClick(workspaceIndex)"
      >
        <v-icon
          v-if="selectedChipIndex === workspaceIndex"
          size="14"
          class="mr-1"
          color="white"
        >mdi-check</v-icon>
        {{ workspace.workSpaceName }}
      </span>
    </div>

    <v-divider></v-divider>
  </v-card>
</template>

<script>
import IssueService from "@/api/service/Issue/IssueService";
import DashBoardFilter from "@/modules/dashboard/Widgets/DashboardFilter.vue";

export default {
  props: ["boardName", "existFilterData", "workSpaceName"],
  components: {
    DashBoardFilter,
  },

  data() {
    return {
      menu: false,
      loading: true,
      downloadMenu: ["Excel", "Pdf", "Json"],
      ProductionData: [],
      search: null,
      searchClicked: false,
      tableData: [],
      finalExportData: [],
      backupTableData: [],
      isDrawer: false,
      switchBtn: "OFF",
      workTimingId: null,
      chipName: null,
      savedFilterData: null,
      selectedChipIndex: null,
      isDragging: false,
      startX: 0,
      scrollStartLeft: 0,
      hasDragged: false,
    };
  },

  created() {
    this.finalExportData = this.tableData;
    this.chipName = this.workSpaceName ? [...this.workSpaceName] : [];
    let saved = localStorage.getItem("filterData");
    if (saved && saved !== "undefined" && saved !== "null") {
      try {
        this.savedFilterData = JSON.parse(saved);
      } catch (e) {
        this.savedFilterData = null;
      }
    }
  },

  mounted() {
    this.attachWheelListener();
  },

  beforeUnmount() {
    this.detachWheelListener();
    window.removeEventListener("mousemove", this.onDrag);
    window.removeEventListener("mouseup", this.stopDrag);
  },

  computed: {
    hasActiveFilter() {
      if (this.selectedChipIndex !== null && this.selectedChipIndex !== undefined) {
        return true;
      }
      const data = this.existFilterData || this.savedFilterData;
      if (!data) return false;
      return Object.values(data).some(
        (v) =>
          v !== null &&
          v !== undefined &&
          (Array.isArray(v)
            ? v.length > 0
            : typeof v === "string"
            ? v.trim() !== ""
            : true)
      );
    },
    isDrawerOpen() {
      return this.$store.getters.getIssueViewDrawer;
    },
    isIssueChanged() {
      return this.$store.getters.getDefaultIssueInView;
    },
  },

  watch: {
    workSpaceName: {
      handler(newVal) {
        if (newVal) {
          this.chipName = [...newVal];
          this.attachWheelListener();
          if (this.selectedChipIndex !== null && this.selectedChipIndex >= newVal.length) {
            this.selectedChipIndex = null;
          }
        }
      },
      deep: true,
      immediate: true,
    },
    existFilterData: {
      handler(newVal) {
        this.savedFilterData = newVal;
      },
      deep: true,
      immediate: true,
    },
    isIssueChanged() {
      this.showIssueView = true;
    },
  },

  methods: {
    attachWheelListener() {
      this.$nextTick(() => {
        const container = this.$refs.chipsContainer;
        if (container && !container.__hasWheelListener) {
          container.addEventListener("wheel", this.onChipsWheel, { passive: false });
          container.__hasWheelListener = true;
        }
      });
    },
    detachWheelListener() {
      const container = this.$refs.chipsContainer;
      if (container && container.__hasWheelListener) {
        container.removeEventListener("wheel", this.onChipsWheel);
        container.__hasWheelListener = false;
      }
    },
    onChipsWheel(e) {
      const container = this.$refs.chipsContainer;
      if (!container) return;
      if (container.scrollWidth <= container.clientWidth) return;

      const delta = Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
      if (delta !== 0) {
        if (e.cancelable) {
          e.preventDefault();
        }
        container.scrollLeft += delta;
      }
    },
    startDrag(e) {
      if (e.button !== 0) return;
      const container = this.$refs.chipsContainer;
      if (!container) return;
      this.isDragging = true;
      this.hasDragged = false;
      this.startX = e.pageX - container.offsetLeft;
      this.scrollStartLeft = container.scrollLeft;
      window.addEventListener("mousemove", this.onDrag);
      window.addEventListener("mouseup", this.stopDrag);
    },
    onDrag(e) {
      if (!this.isDragging) return;
      const container = this.$refs.chipsContainer;
      if (!container) return;
      const x = e.pageX - container.offsetLeft;
      const distance = x - this.startX;
      if (Math.abs(distance) > 4) {
        this.hasDragged = true;
        if (e.cancelable) {
          e.preventDefault();
        }
        container.scrollLeft = this.scrollStartLeft - distance;
      }
    },
    stopDrag() {
      if (!this.isDragging) return;
      this.isDragging = false;
      window.removeEventListener("mousemove", this.onDrag);
      window.removeEventListener("mouseup", this.stopDrag);
      setTimeout(() => {
        this.hasDragged = false;
      }, 60);
    },
    handleChipClick(index) {
      if (this.hasDragged) return;
      this.changeIndex(index);
    },
    changeIndex(index) {
      if (this.selectedChipIndex === index) {
        this.selectedChipIndex = null;
        this.$emit("scroll", null);
      } else {
        this.selectedChipIndex = index;
        this.$emit("scroll", index);
      }
    },
    clearAllFilters() {
      this.selectedChipIndex = null;
      localStorage.removeItem("filterData");
      this.savedFilterData = null;
      this.$emit("applyChange", null);
      this.$emit("scroll", null);
      this.menu = false;
    },
    applyChange($event) {
      if (
        $event &&
        Object.values($event).some(
          (v) =>
            v !== null &&
            v !== undefined &&
            (Array.isArray(v)
              ? v.length > 0
              : typeof v === "string"
              ? v.trim() !== ""
              : true)
        )
      ) {
        localStorage.setItem("filterData", JSON.stringify($event));
        this.savedFilterData = $event;
        this.$emit("applyChange", $event);
      } else {
        localStorage.removeItem("filterData");
        this.savedFilterData = null;
        this.$emit("applyChange", null);
      }
      this.menu = false;
    },
    gettimingId(id) {
      this.workTimingId = id;
    },
    async getFilterData(data) {
      this.filterData = data;
      this.filterDialog = false;
      this.getissuetype(this.filterData);
      this.menu = false;
    },
    async getissuetype($obj) {
      let obj = {};
      if ($obj) {
        obj = $obj;
      }
      obj.next_action = this.userId;
      var res = await IssueService.getAssignToMeData(obj);
      this.issueType = res && res.length ? res : [];
    },
    togglePastDue() {
      this.switchBtn = this.switchBtn === "ON" ? "OFF" : "ON";
      this.getSwitchData();
    },
    getSwitchData() {
      this.$emit("show-zero-issue", this.switchBtn);
    },
    toggleSearch() {
      this.searchClicked = !this.searchClicked;
      if (this.searchClicked) {
        document.addEventListener("click", this.handleOutsideClick);
      } else {
        document.removeEventListener("click", this.handleOutsideClick);
      }
    },
    handleOutsideClick(event) {
      const textField = this.$refs.searchContainer;
      if (textField && !textField.contains(event.target)) {
        this.searchClicked = false;
        document.removeEventListener("click", this.handleOutsideClick);
      }
    },
    getSearch() {
      this.$emit("search-data", this.search);
    },
    clearSearch() {
      this.search = null;
      this.$emit("search-data", null);
    },
    addData(data) {
      this.finalExportData = data;
    },
    async exportFile(type) {
      this.$emit("export-data", type);
    },
  },
};
</script>

<style scoped>
.common-board-header-card {
  background-color: #ffffff;
  flex-shrink: 0;
}

.header-main-bar {
  min-height: 52px;
}

.left-header-section {
  gap: 16px;
}

.board-header-title {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  letter-spacing: -0.2px;
  white-space: nowrap;
}

.search-input-box {
  width: 250px;
}

.search-input-box :deep(.v-field) {
  border-radius: 6px;
  font-size: 13px;
}

.right-header-section {
  gap: 12px;
}

.clear-filter-btn {
  font-size: 12px;
  font-weight: 500;
  border-radius: 6px;
  height: 32px;
}

.switch-box {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
  cursor: pointer;
}

.header-switch {
  display: inline-flex !important;
  align-items: center;
  flex: 0 0 auto !important;
  width: auto !important;
}

.header-switch :deep(.v-input__control) {
  width: auto !important;
  flex: 0 0 auto !important;
}

.header-switch :deep(.v-selection-control) {
  min-height: unset !important;
  height: 32px !important;
  width: auto !important;
  flex: 0 0 auto !important;
}

.switch-label {
  white-space: nowrap !important;
  font-size: 13px;
  font-weight: 500;
  color: #334155;
  user-select: none;
  cursor: pointer;
  line-height: 1;
}

.workspace-chips-bar {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  gap: 8px;
  width: 100%;
  padding-bottom: 6px;
  padding-top: 4px;
  cursor: grab;
  user-select: none;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f8fafc;
}

.workspace-chips-bar:active {
  cursor: grabbing;
}

.workspace-chips-bar::-webkit-scrollbar {
  height: 5px;
}
.workspace-chips-bar::-webkit-scrollbar-track {
  background: #f8fafc;
  border-radius: 4px;
}
.workspace-chips-bar::-webkit-scrollbar-thumb {
  background-color: #cbd5e1;
  border-radius: 4px;
}
.workspace-chips-bar::-webkit-scrollbar-thumb:hover {
  background-color: #94a3b8;
}

.workspace {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  max-height: 28px;
  background-color: #fdf4f4;
  border: 1.5px solid #ffa1a1;
  padding: 0 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #333333;
  white-space: nowrap !important;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;
  flex-shrink: 0;
  box-sizing: border-box;
}

.workspace:hover {
  background-color: #fee2e2;
  border-color: #f87171;
}

.workspace.workspace-selected {
  background-color: #ef4444 !important;
  border-color: #dc2626 !important;
  color: #ffffff !important;
  box-shadow: 0 1px 3px rgba(239, 68, 68, 0.35);
}

.workspace.workspace-selected:hover {
  background-color: #dc2626 !important;
  border-color: #b91c1c !important;
}

.show-cursor {
  cursor: pointer;
}
</style>
